'use client';

import { Badge } from '@ophelos/ui/components/badge';
import { Card, CardChevron, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card-layer';
import { Separator } from '@ophelos/ui/components/separator';
import { Clock } from 'lucide-react';
import { useState } from 'react';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import type { DebtData, LegacyEvent } from '@/types';
import { formatLedgerDate } from './charges';
import { TransactionRow } from './TransactionsList';

/**
 * Migrated debts with something to show. A migration with no events has nothing to
 * explain — its opening and transferred balances are the same figure — so it gets no card.
 */
export function migratedDebts(debts: DebtData[]): DebtData[] {
  return debts.filter((debt) => debt.migratedHistory?.events.length);
}

/** Oldest first, matching the balance timeline's reading order. */
function byDate(a: LegacyEvent, b: LegacyEvent): number {
  return a.occurredOn.localeCompare(b.occurredOn);
}

/**
 * A balance line — the opening figure and the transferred figure, which are not events.
 * Dated like the rows between them so the whole card reads as one timeline.
 */
function BalanceRow({
  label,
  date,
  amount,
  currency,
  emphasis,
}: { label: string; date: string; amount: number; currency: string; emphasis?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 px-3">
      <div className="min-w-0">
        <p className={`text-sm ${emphasis ? 'font-semibold' : 'font-medium'}`}>{label}</p>
        <p className="text-sm text-muted-foreground">{date}</p>
      </div>
      <CurrencyDisplay amount={amount} currency={currency} className={`text-sm shrink-0 ${emphasis ? 'font-bold' : 'font-medium'}`} />
    </div>
  );
}

interface MigratedHistoryCardProps {
  debt: DebtData;
  /**
   * Names the debt under the heading. Needed wherever more than one card can appear —
   * an account with several migrated debts renders one heading per debt.
   */
  showDebt?: boolean;
  /** Opens on mount, for arrivals from the "see your earlier payments" link. */
  defaultOpen?: boolean;
}

/**
 * A migrated debt's history on Ophelos V1.
 *
 * Collapsed it is one line: what transferred, and when. Opened it reads like the
 * transactions tab it sits on — same row component — bracketed by the opening balance
 * and the amount that came across, so the customer can see how one became the other.
 *
 * Every figure here is a record. The transferred amount is the migration snapshot rather
 * than a sum of the rows — it is the balance the migration asserted, and the one Forest
 * actually opened the case at.
 */
export function MigratedHistoryCard({ debt, showDebt = false, defaultOpen = false }: MigratedHistoryCardProps) {
  const { t, currentLanguage } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(defaultOpen);
  const history = debt.migratedHistory;

  if (!history || history.events.length === 0) return null;

  const events = [...history.events].sort(byDate);

  return (
    <Card isCollapsible isExpanded={isExpanded} onToggle={() => setIsExpanded((open) => !open)} variant="fullscreen" className="gap-5">
      <CardHeader>
        <CardChevron />
        <CardTitle className="flex items-center justify-between gap-3 w-full">
          <span>{t('migrated.transferredFrom')}</span>
          <CurrencyDisplay amount={history.carriedOverAmount} currency={debt.currency} className="font-semibold shrink-0" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 px-3">
          <Badge variant="subtle">
            <Clock className="size-3.5" />
            {t('migrated.recordOnly')}
          </Badge>
          {showDebt && (
            <span className="text-sm text-muted-foreground">
              {[debt.description, debt.externalCaseRef ?? debt.accountNumber].filter(Boolean).join(' • ')}
            </span>
          )}
        </div>

        <BalanceRow
          label={t('migrated.openingBalance')}
          date={formatLedgerDate(history.openedOn, currentLanguage)}
          amount={history.previousOriginalAmount}
          currency={debt.currency}
        />

        <Separator />

        {events.map((event, index) => (
          <TransactionRow
            // Index-qualified: nothing constrains references inside the events array, and a
            // duplicate would otherwise collide as a React key.
            key={`${index}:${event.reference}`}
            title={t(`migrated.event.${event.type}`)}
            date={formatLedgerDate(event.occurredOn, currentLanguage)}
            method={t(`paymentMethod.${event.method}`, { defaultValue: '' })}
            amount={Math.abs(event.amount)}
            lowersBalance={event.amount < 0}
            currency={debt.currency}
            // These settled on V1, not with us — no completion badge.
            showCompleted={false}
          />
        ))}

        <Separator />

        <BalanceRow
          label={t('migrated.amountTransferred')}
          date={formatLedgerDate(history.migratedOn, currentLanguage)}
          amount={history.carriedOverAmount}
          currency={debt.currency}
          emphasis
        />

        <p className="text-sm text-muted-foreground px-3 pt-2">
          {t('migrated.transferredOn', {
            date: formatLedgerDate(history.migratedOn, currentLanguage),
          })}
        </p>
      </CardContent>
    </Card>
  );
}
